import { useCallback, useSyncExternalStore } from 'react';

/**
 * 安裝到主畫面。
 *
 * **各平台能做到什麼（這決定了整支的形狀）**
 *
 * | 平台                         | 能力                                        |
 * | ---------------------------- | ------------------------------------------- |
 * | Android Chrome / Edge / 三星 | `beforeinstallprompt` → 我們自己放按鈕觸發   |
 * | iOS 全部瀏覽器（都是 WebKit）| **沒有 API**，只能教「分享 → 加入主畫面」    |
 * | 桌機 Chrome / Edge           | 同 Android，但本專案不提示（手機導向）       |
 *
 * **靜默安裝在任何平台都做不到**：`prompt()` 必須由使用者手勢觸發，而且系統一定會
 * 再跳一次確認。這是瀏覽器刻意的限制，不是少寫了什麼。
 *
 * **為什麼監聽器掛在模組層級而不是 effect 裡**
 * `beforeinstallprompt` 由瀏覽器在確認 manifest 與 SW 都就緒後**主動**送出，時機
 * 早於使用者互動、也可能早於某個元件掛載。等到 effect 才註冊，那一次事件就錯過了，
 * 而它**不會重送**——症狀是「安裝按鈕在某些次載入時就是不出現」。
 */

/** 尚未進入 TypeScript DOM lib 的事件（Chromium 專屬）。 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PwaPlatform = 'ios' | 'android' | 'other';

/**
 * iOS 上是哪一個瀏覽器。
 *
 * **加入主畫面的入口位置各家不同**，寫死成 Safari 的講法會把其他瀏覽器的使用者
 * 送去一個不存在的按鈕：
 *
 * - `safari`：分享鍵在**下方工具列**
 * - `chromium`（Chrome／Edge，iOS 17 起）：分享鍵在**網址列**
 * - `unsupported`（Firefox 等）：根本沒有這個選項，只能請他改用 Safari 開啟
 *
 * 全部都是 WebKit，差的是外殼 app 的介面，不是引擎能力。
 */
export type IosBrowser = 'safari' | 'chromium' | 'unsupported';

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface UsePwaInstallReturn {
  /** 已在 standalone 模式執行（已加入主畫面） */
  readonly isInstalled: boolean;
  readonly platform: PwaPlatform;
  /** 只有 `platform === 'ios'` 時有意義，決定手動步驟怎麼寫 */
  readonly iosBrowser: IosBrowser;
  /** 可以按按鈕直接叫出安裝流程（目前只有 Chromium 系列給得起） */
  readonly canPrompt: boolean;
  /**
   * 叫出瀏覽器的安裝確認。
   *
   * 回 `'unavailable'` 代表這個瀏覽器沒給我們事件（iOS、或事件已用掉），
   * 呼叫端應改為顯示手動步驟。
   */
  readonly promptInstall: () => Promise<InstallOutcome>;
}

/* ─── 攔截 beforeinstallprompt ─────────────────────────────────────────────── */

let deferredPrompt: BeforeInstallPromptEvent | null = null;

/**
 * 這一次瀏覽期間裝好了。
 *
 * 需要單獨記一筆，是因為**從瀏覽器分頁安裝之後，這個分頁仍然是瀏覽器分頁**：
 * `display-mode: standalone` 與 `navigator.standalone` 都還是 false，要等下一次
 * 從主畫面啟動才會翻轉。少了這個旗標，使用者剛裝完，提示卡片還會繼續掛在那裡
 * 叫他去安裝。
 */
let installedInSession = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    /*
     * 一定要 preventDefault：Chrome 預設會自己在畫面底部彈一條 mini-infobar，
     * 與我們的提示卡片重疊，而且它的文案與時機我們控制不了。
     */
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });

  /* 安裝完成（可能是從我們的按鈕，也可能是使用者自己從選單裝的）→ 收起提示 */
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installedInSession = true;
    emit();
  });
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/* ─── 是否已安裝 ───────────────────────────────────────────────────────────── */

const STANDALONE_QUERY = '(display-mode: standalone)';

/**
 * 訂閱顯示模式。
 *
 * 從瀏覽器分頁安裝後，**當前這一個分頁仍然是瀏覽器分頁**，不會變成 standalone；
 * 真正讓這個值翻轉的是 `appinstalled`（我們一併訂閱）與下一次從主畫面啟動。
 */
function subscribeInstalled(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const media = window.matchMedia(STANDALONE_QUERY);
  media.addEventListener('change', onChange);
  /* 直接訂閱事件而非只靠 listeners：兩個快照各自訂閱，互不依賴誰先被呼叫 */
  window.addEventListener('appinstalled', onChange);

  return () => {
    media.removeEventListener('change', onChange);
    window.removeEventListener('appinstalled', onChange);
  };
}

function getIsInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  if (installedInSession) return true;

  /*
   * iOS 不支援 display-mode: standalone 的偵測，改看 Safari 專屬的
   * `navigator.standalone`。少了這一條，已經加到主畫面的 iPhone 使用者每次開 app
   * 都還會被提示安裝一次。
   */
  const iosStandalone = (navigator as Navigator & { standalone?: boolean })
    .standalone;

  return window.matchMedia(STANDALONE_QUERY).matches || iosStandalone === true;
}

/* ─── 平台 ─────────────────────────────────────────────────────────────────── */

function detectPlatform(): PwaPlatform {
  if (typeof navigator === 'undefined') return 'other';

  const ua = navigator.userAgent;

  /*
   * iPadOS 13 起的 Safari 預設回報 Macintosh，只能靠「有觸控點」再分辨一次。
   * 漏掉它，iPad 使用者會落進 'other' 而完全看不到提示。
   */
  const isIpadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;

  if (/iPad|iPhone|iPod/.test(ua) || isIpadOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/**
 * iOS 上的瀏覽器外殼。
 *
 * UA 裡的 `CriOS`／`EdgiOS`／`FxiOS` 是各家在 iOS 上的識別碼——它們全都是 WebKit，
 * 所以 `Safari` 這個字每一家都有，不能拿來判斷。
 */
function detectIosBrowser(): IosBrowser {
  if (typeof navigator === 'undefined') return 'safari';

  const ua = navigator.userAgent;

  if (/CriOS|EdgiOS/.test(ua)) return 'chromium';
  if (/FxiOS|OPiOS|mercury/i.test(ua)) return 'unsupported';

  return 'safari';
}

/* 平台與瀏覽器在同一次瀏覽中不會變，算一次即可 */
const platform = detectPlatform();
const iosBrowser = detectIosBrowser();

export function usePwaInstall(): UsePwaInstallReturn {
  const isInstalled = useSyncExternalStore(
    subscribeInstalled,
    getIsInstalled,
    () => false,
  );

  const prompt = useSyncExternalStore(
    subscribe,
    () => deferredPrompt,
    () => null,
  );

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    if (!deferredPrompt) return 'unavailable';

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    /*
     * 事件只能用一次，用完就作廢——留著它，第二次按下去會拋
     * 「The prompt() method may only be called once」。被婉拒的人下次重新載入
     * 若仍符合條件，瀏覽器會再送一次新的事件。
     */
    deferredPrompt = null;
    emit();

    return outcome;
  }, []);

  return {
    isInstalled,
    platform,
    iosBrowser,
    canPrompt: prompt !== null,
    promptInstall,
  };
}
